import { useContext, useLayoutEffect, useMemo, useState } from "react";
import CustomizerContext from "../../../../../_helper/Customizer";
import {
    Description,
    ExpenseDescription,
    ExpenseCategory,
    ExpenseGroup,
    ExpenseClass,
    ExpenseType,
    Name,
    Status,
    CreatedBy,
    ModifiedBy,
} from "../../../../../Constant";
import {
    financesExpenseCategoryApi,
    financesExpenseClassApi,
    financesExpenseDescriptionApi,
    financesExpenseGroupApi,
    financesExpenseTypeApi,
} from "../../../../../api";
import { createDropdownField } from "../../../../../Data/FormField/PublicData";
import useAxiosPrivate from "../../../../../Hooks/useAxiosPrivate";
import { IExpenseDescriptionResponse } from "../ExpenseDescription.types";
import useAuth from "../../../../../Hooks/useAuth";
import { formatDateWithTimeZone } from "../../../../../_helper/dateHelper";
import { StatusRow } from "../../ReportType/List/ReportTypeList";
import { toast } from "react-toastify";

export default function useExpenseDescriptionListController() {
    const axiosPrivate = useAxiosPrivate();
    const { userOrganisationId }: any = useAuth();
    const title = ExpenseDescription;
    const [formFields, setFormFields] = useState<any[]>([
        {
            id: "name",
            name: "name",
            label: Name,
            type: "text",
            value: "",
            required: true,
            colSize: 6,
        },

        {
            id: "description",
            name: "description",
            label: Description,
            type: "textarea",
            value: "",
            required: true,
            colSize: 6,
        },

        {
            id: "is_active",
            name: "is_active",
            label: Status,
            type: "switch",
            value: "1",
            colSize: 6,
        },
    ]);

    //?? DROPDOWNS INITS >
    const expenseCategoryDropDown = createDropdownField(
        axiosPrivate,
        "expense_category",
        "expense_category",
        ExpenseCategory,
        `${financesExpenseCategoryApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`,
        "",
        6
    );
    expenseCategoryDropDown.onChange = async (id, updatedFields) => {
        const nextField = updatedFields.find(
            (field: any) => field.id === "expense_group"
        );
        if (nextField) {
            const endPoint = `${financesExpenseGroupApi}?organisation_id=${userOrganisationId}&expense_category_id=${id}`
            await nextField.fetchOptions(id, false, endPoint); 
            const updatedFormFields = updatedFields.map((field: any) => {
                if (field.id === "expense_category") {
                    return { ...field, value: id };
                }
                return field;
            });
            setFormFields([...updatedFormFields]);
        }
    };

    const expenseGroupDropDown = createDropdownField(
        axiosPrivate,
        "expense_group",
        "expense_group",
        ExpenseGroup,
        ``,
        "",
        6
    );
    expenseGroupDropDown.onChange = async (id, updatedFields) => {
        const nextField = updatedFields.find(
            (field: any) => field.id === "expense_class"
        );
        if (nextField) {
            const endPoint = `${financesExpenseClassApi}?organisation_id=${userOrganisationId}&expense_group_id=${id}`
            await nextField.fetchOptions(id, false, endPoint);
            const updatedFormFields = updatedFields.map((field: any) => {
                if (field.id === "expense_group") {
                    return { ...field, value: id };
                }
                return field;
            });
            setFormFields([...updatedFormFields]);
        }
    };

    const expenseClassDropDown = createDropdownField(
        axiosPrivate,
        "expense_class",
        "expense_class",
        ExpenseClass,
        `${financesExpenseClassApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`,
        "",
        6
    );
    expenseClassDropDown.onChange = async (id, updatedFields) => {
        const nextField = updatedFields.find(
            (field: any) => field.id === "expense_type_id"
        );
        if (nextField) {
            const endPoint = `${financesExpenseTypeApi}?organisation_id=${userOrganisationId}&expense_class_id=${id}`
            await nextField.fetchOptions(id, false, endPoint);
            const updatedFormFields = updatedFields.map((field: any) => {
                if (field.id === "expense_class") {
                    return { ...field, value: id };
                }
                return field;
            });
            setFormFields([...updatedFormFields]);
        }
    };

    const expenseTypeDropDown = createDropdownField(
        axiosPrivate,
        "expense_type_id",
        "expense_type_id",
        ExpenseType,
        ``,
        "",
        6
    );

    useLayoutEffect(() => {
        //?? Updates all fields once component is mounted
        (async function () {
            const updatedFields = [
                expenseCategoryDropDown,
                expenseGroupDropDown,
                expenseClassDropDown,
                expenseTypeDropDown,
                ...formFields,
            ];
            await expenseCategoryDropDown.fetchOptions();
            setFormFields(updatedFields);
        })();
    }, []);

    const tableColumns = [
        {
            name: Name,
            selector: (row: (typeof data)[0]) => `${row.name}`,
            sortable: true,
            center: false,
        },

        {
            name: Description,
            selector: (row: (typeof data)[0]) => `${row.description}`,
            sortable: false,
            center: false,
        },

        {
            name: ExpenseCategory,
            selector: (row: (typeof data)[0]) =>
                `${row.expense_type.expense_class.expense_group.expense_category.name}`,
            sortable: true,
            center: false,
        },

        {
            name: ExpenseGroup,
            selector: (row: (typeof data)[0]) =>
                `${row.expense_type.expense_class.expense_group.name}`,
            sortable: true,
            center: false,
        },

        {
            name: ExpenseClass,
            selector: (row: (typeof data)[0]) =>
                `${row.expense_type.expense_class.name}`,
            sortable: true,
            center: false,
        },

        {
            name: ExpenseType,
            selector: (row: (typeof data)[0]) => `${row.expense_type.name}`,
            sortable: true,
            center: false,
        },

        {
            name: Status,
            selector: (row: (typeof data)[0]) => `${row.is_active}`,
            sortable: true,
            center: true,
            cell: (row: (typeof data)[0]) => StatusRow(row),
        },

        {
            name: CreatedBy,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.created_at)}`,
            sortable: false,
            center: false,
        },

        {
            name: ModifiedBy,
            selector: (row: (typeof data)[0]) =>
                `${formatDateWithTimeZone(row.updated_at)}`,
            sortable: false,
            center: false,
        },
    ];

    function fetchData() {
        setLoading(true);
        axiosPrivate
            .get(
                `${financesExpenseDescriptionApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}`
            )
            .then((res) => {
                const response: IExpenseDescriptionResponse[] = res.data;
                setData(response);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 404) {
                  toast.error(err.response.data.detail);
                } else {
                  toast.error('Something Went Wrong');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useLayoutEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row: (typeof data)[0] | any) => {
        setViewModalOpen(true);
        setEditMode(true);
        
        row.expense_category =
            row.expense_type.expense_class.expense_group.expense_category.id;
        expenseCategoryDropDown.onChange(
            row.expense_type.expense_class.expense_group.expense_category.id,
            formFields
        );

        row.expense_group = row.expense_type.expense_class.expense_group.id;
        expenseGroupDropDown.onChange(
            row.expense_type.expense_class.expense_group.id,
            formFields
        );

        row.expense_class = row.expense_type.expense_class.id;
        expenseClassDropDown.onChange(row.expense_class, formFields);

        row.expense_type_id = row.expense_type.id;
        expenseTypeDropDown.onChange(row.expense_type_id, formFields);

        setSelectedRowData(row);
    };

    const { layoutURL } = useContext(CustomizerContext);
    const newBtn = `${process.env.PUBLIC_URL}/finance/settings/expense-description/create/${layoutURL}`;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IExpenseDescriptionResponse[]>([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<
        (typeof data)[0] | null
    >(null);
    const [editMode, setEditMode] = useState(false);

    const handleView = (row: (typeof data)[0]) => {
        setViewModalOpen(true);
        setEditMode(false);
        setSelectedRowData(row);
    };

    const handleEditRec = async (
        id: number | string,
        editRecord: (typeof data)[0] | any
    ) => {
        await axiosPrivate
            .put(`${financesExpenseDescriptionApi}/${id}`, editRecord)
            .then((_res) => {
                const updatedData = data.map((row) =>
                    row.id === selectedRowData!.id ? { ...row, ..._res.data } : row
                );
                setData(updatedData);
                toast.success(`${_res.data.name} Editted Successfully.`)
                //fetchData();
            })
            .catch((err) => {
              if (err.response && err.response.status === 422) {
                toast.error('Validation Errors');
              } else if (err.response && err.response.status === 400) {
                toast.error(err.response.data.detail);
              } else {
                toast.error('Something Went Wrong');
              }
              throw err;
              //toast.error(err.response.message || "Whoops! Something went wrong");
            });
    };

    const memoizedHandleEdit = useMemo(
        () => handleEditRec,
        [data, selectedRowData]
    );

    return {
        data,
        tableColumns,
        viewModalOpen,
        selectedRowData,
        editMode,
        newBtn,
        title,
        formFields,
        setViewModalOpen,
        handleView,
        handleEdit,
        memoizedHandleEdit,
    };
}
